import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  ClipboardList, Plus, Search, Loader2, Eye, Edit3, Trash2, ArrowRight, ArrowLeft,
  X, CheckCircle2, XCircle, FileText, Send, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  rascunho:  { label: "RASCUNHO",   cls: "bg-slate-100 text-slate-700 border-slate-300" },
  enviada:   { label: "ENVIADA",    cls: "bg-blue-100 text-blue-800 border-blue-300" },
  aprovada:  { label: "APROVADA",   cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  os_gerada: { label: "OS GERADA",  cls: "bg-orange-100 text-orange-800 border-orange-300" },
  concluida: { label: "CONCLUÍDA",  cls: "bg-slate-200 text-slate-800 border-slate-300" },
  recusada:  { label: "RECUSADA",   cls: "bg-red-100 text-red-700 border-red-300" },
};

const TIPO_SERVICO = [
  { value: "corretivo", label: "Corretivo" },
  { value: "preventivo", label: "Preventivo" },
  { value: "eletrico", label: "Elétrico" },
  { value: "mecanico", label: "Mecânico" },
  { value: "hidraulico", label: "Hidráulico" },
  { value: "outro", label: "Outro" },
];

const fmtBRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || { label: status, cls: "bg-slate-100" };
  return <Badge className={`${s.cls} border text-[10px] font-mono`}>{s.label}</Badge>;
}

// ════════════════════════════════════════════════════════════════════════
//  WIZARD 3 PASSOS — Nova proposta
// ════════════════════════════════════════════════════════════════════════
function WizardModal({ open, onOpenChange, onSaved, editar = null }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Passo 1
  const [busca, setBusca] = useState("");
  const [sugestoes, setSugestoes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [frota, setFrota] = useState([]);
  const [equipamento, setEquipamento] = useState(null);
  const [tipoServico, setTipoServico] = useState("corretivo");
  const [descProblema, setDescProblema] = useState("");

  // Passo 2
  const [itensBusca, setItensBusca] = useState("");
  const [itensSug, setItensSug] = useState([]);
  const [itens, setItens] = useState([]);
  const [descontoPct, setDescontoPct] = useState("");
  const [formaPag, setFormaPag] = useState("a_vista");
  const [vencimento, setVencimento] = useState("");
  const [previsaoFat, setPrevisaoFat] = useState("");
  const debounceRef = useRef(null);
  const [buscando, setBuscando] = useState(false);
  const [buscaErro, setBuscaErro] = useState("");
  const [tocado, setTocado] = useState({});

  useEffect(() => {
    if (open) {
      setStep(1);
      setBusca(""); setSugestoes([]); setCliente(null); setFrota([]);
      setEquipamento(null); setTipoServico("corretivo"); setDescProblema("");
      setItensBusca(""); setItensSug([]); setItens([]);
      setDescontoPct(""); setFormaPag("a_vista"); setVencimento(""); setPrevisaoFat("");
      setBuscaErro(""); setTocado({});
      if (editar) {
        const cli = { id: editar.cliente_id, nome: editar.cliente_nome, cnpj_cpf: editar.cliente_cnpj };
        setCliente(cli);
        setBusca(editar.cliente_nome || "");
        setTipoServico(editar.tipo_servico || "corretivo");
        setDescProblema(editar.descricao_problema || "");
        setItens((editar.itens || []).map((it) => ({ ...it })));
        setDescontoPct(editar.desconto_percentual ? String(editar.desconto_percentual) : "");
        setFormaPag(editar.forma_pagamento || "a_vista");
        setVencimento(editar.vencimento || "");
        setPrevisaoFat(editar.previsao_faturamento || "");
        api.get(`/posvenda/cliente/${editar.cliente_id}/frota`).then(({ data }) => {
          const fr = data.items || [];
          setFrota(fr);
          setEquipamento(fr.find((e) => String(e.id) === String(editar.equipamento_id)) || null);
        }).catch(() => setFrota([]));
      }
    }
  }, [open, editar]);

  // Busca de clientes (debounce 300ms)
  useEffect(() => {
    if (!busca || busca.length < 2 || cliente) { setSugestoes([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      setBuscaErro("");
      try {
        const { data } = await api.get("/clientes-busca", { params: { q: busca, limit: 12 } });
        setSugestoes(data.items || []);
        if (!(data.items || []).length) setBuscaErro("Nenhum cliente encontrado. Busque pelo nome ou pelo CNPJ");
      } catch (e) {
        setSugestoes([]);
        setBuscaErro(e?.response?.data?.detail || "Falha na busca de clientes. Tente novamente");
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [busca, cliente]);

  const selecionarCliente = async (cli) => {
    setCliente(cli);
    setBusca(cli.nome_fantasia || cli.razao_social || cli.nome);
    setSugestoes([]);
    // carregar frota
    try {
      const cid = cli.id;
      const { data } = await api.get(`/posvenda/cliente/${cid}/frota`);
      setFrota(data.items || []);
    } catch (e) { setFrota([]); }
  };

  // Busca de itens (peças/serviços)
  useEffect(() => {
    if (!itensBusca || itensBusca.length < 2) { setItensSug([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/cadastro/pecas-servicos", {
          params: { q: itensBusca, status: "ativo", limit: 10 },
        });
        setItensSug(data.items || []);
      } catch (e) { setItensSug([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [itensBusca]);

  const adicionarItem = (it) => {
    setItens((arr) => [...arr, {
      item_id: it.id,
      codigo: it.codigo,
      descricao: it.descricao,
      unidade: it.unidade,
      tipo: it.tipo,
      quantidade: 1,
      valor_unitario: it.valor_unitario,
    }]);
    setItensBusca("");
    setItensSug([]);
  };

  const atualizarItem = (idx, campo, valor) => {
    setItens((arr) => arr.map((it, i) => i === idx ? { ...it, [campo]: valor } : it));
  };

  const removerItem = (idx) => setItens((arr) => arr.filter((_, i) => i !== idx));

  const totais = useMemo(() => {
    const subtotal = itens.reduce((s, it) => s + (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0), 0);
    const desc = subtotal * (parseFloat(descontoPct || "0") / 100);
    return { subtotal, desconto: desc, total: Math.max(0, subtotal - desc) };
  }, [itens, descontoPct]);

  const proximoPasso = () => {
    if (step === 1) {
      setTocado({ cliente: true, desc: true });
      if (!cliente) return toast.error("Selecione um cliente da lista");
      if (!descProblema || descProblema.trim().length < 5) return toast.error("Descreva o problema com pelo menos 5 caracteres");
      setStep(2);
    } else if (step === 2) {
      if (itens.length === 0) return toast.error("Adicione ao menos um item");
      setStep(3);
    }
  };

  const salvar = async (enviar = false) => {
    setSaving(true);
    try {
      const payload = {
        ...(editar ? {} : { cliente_id: cliente.id }),
        equipamento_id: equipamento?.id ? String(equipamento.id) : null,
        equipamento_label: equipamento ? `${equipamento.modelo || ""} ${equipamento.serial || ""}`.trim() : null,
        tipo_servico: tipoServico,
        descricao_problema: descProblema,
        itens: itens.map((it) => ({
          item_id: it.item_id,
          codigo: it.codigo,
          descricao: it.descricao,
          unidade: it.unidade,
          tipo: it.tipo,
          quantidade: parseFloat(String(it.quantidade).replace(",", ".")) || 0,
          valor_unitario: parseFloat(String(it.valor_unitario).replace(",", ".")) || 0,
        })),
        desconto_percentual: parseFloat(descontoPct || "0") || 0,
        forma_pagamento: formaPag,
        vencimento: vencimento || null,
        previsao_faturamento: previsaoFat || null,
      };
      if (editar) {
        await api.patch(`/posvenda/propostas/${editar.id}`, payload);
        toast.success("Proposta atualizada");
        onSaved && onSaved(editar);
        onOpenChange(false);
        return;
      }
      const { data: novaProposta } = await api.post("/posvenda/propostas", payload);
      if (enviar) {
        await api.patch(`/posvenda/propostas/${novaProposta.id}/status`, { novo_status: "enviada" });
      }
      toast.success(enviar ? "Proposta enviada ao cliente" : "Rascunho salvo");
      onSaved && onSaved(novaProposta);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar proposta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="wizard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-600" />
            {editar ? `Editar proposta ${editar.numero}` : "Nova Proposta Pós-venda"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Passo {step} de 3 — {step === 1 ? "Cliente e Equipamento" : step === 2 ? "Itens da Proposta" : "Revisão e Envio"}
          </DialogDescription>
        </DialogHeader>

        {/* Stepper visual */}
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3].map((n) => (
            <React.Fragment key={n}>
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-mono ${
                step >= n ? "bg-orange-600 text-white" : "bg-slate-200 text-slate-600"
              }`}>{n}</div>
              {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-orange-600" : "bg-slate-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* PASSO 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Cliente</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  data-testid="wizard-cliente-busca"
                  className="pl-8"
                  placeholder="Buscar por nome ou CNPJ..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setTocado((t) => ({ ...t, cliente: true })); if (cliente) setCliente(null); }}
                  disabled={!!editar}
                />
                {buscando && <Loader2 className="w-4 h-4 absolute right-2 top-2.5 animate-spin text-slate-400" />}
                {sugestoes.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {sugestoes.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => selecionarCliente(s)}
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b text-sm"
                        data-testid={`sug-cli-${s.id}`}
                      >
                        <div className="font-medium">{s.nome_fantasia || s.razao_social || s.nome}</div>
                        <div className="text-xs text-slate-500">
                          CNPJ {s.cnpj || s.cnpj_cpf || "não informado"}{s.uf ? ` · ${s.uf}` : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {!cliente && busca.length >= 2 && !buscando && buscaErro && (
                <div className="mt-1 text-xs text-amber-700" data-testid="wizard-cliente-feedback">{buscaErro}</div>
              )}
              {!cliente && tocado.cliente && !buscaErro && sugestoes.length > 0 && (
                <div className="mt-1 text-xs text-slate-500">Selecione um cliente da lista</div>
              )}
              {!cliente && tocado.cliente && busca.length < 2 && (
                <div className="mt-1 text-xs text-red-600" data-testid="wizard-cliente-obrigatorio">Cliente obrigatório. Digite ao menos 2 caracteres</div>
              )}
              {cliente && (
                <div className="mt-2 p-2 bg-emerald-50 rounded text-xs flex items-start justify-between" data-testid="wizard-cliente-selecionado">
                  <div>
                    <div className="font-medium text-emerald-900">{cliente.nome_fantasia || cliente.razao_social || cliente.nome}</div>
                    <div className="text-emerald-700">CNPJ {cliente.cnpj || cliente.cnpj_cpf || "não informado"}{cliente.uf ? ` · ${cliente.uf}` : ""}</div>
                  </div>
                  {!editar && (
                    <button onClick={() => { setCliente(null); setBusca(""); setFrota([]); setEquipamento(null); }}
                      className="text-emerald-700 hover:text-emerald-900" data-testid="wizard-cliente-limpar">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {cliente && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Equipamento (opcional)</Label>
                {frota.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">Nenhum equipamento ativo na frota deste cliente</div>
                ) : (
                  <Select value={equipamento?.id ? String(equipamento.id) : ""} onValueChange={(v) => {
                    const eq = frota.find((e) => String(e.id) === v);
                    setEquipamento(eq || null);
                  }}>
                    <SelectTrigger data-testid="wizard-equipamento-select"><SelectValue placeholder="Selecione um equipamento da frota" /></SelectTrigger>
                    <SelectContent>
                      {frota.map((eq) => (
                        <SelectItem key={eq.id} value={String(eq.id)}>
                          {eq.modelo || "—"} · Série: {eq.serial || "—"} {eq.placa ? `· ${eq.placa}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div>
              <Label className="text-[10px] uppercase tracking-wider">Tipo de serviço</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="wizard-tipo-servico">
                {TIPO_SERVICO.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTipoServico(t.value)}
                    data-testid={`wizard-tipo-${t.value}`}
                    className={`px-3 py-1.5 rounded-md border text-sm transition-colors ${
                      tipoServico === t.value ? "bg-orange-600 border-orange-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-orange-400"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider">Descrição do problema / solicitação</Label>
              <Textarea
                data-testid="wizard-descricao"
                rows={4}
                value={descProblema}
                onChange={(e) => { setDescProblema(e.target.value); setTocado((t) => ({ ...t, desc: true })); }}
                placeholder="Descreva o que precisa ser feito (ex: troca do acrílico de proteção do EJC216)"
              />
              {tocado.desc && descProblema.trim().length < 5 && (
                <div className="mt-1 text-xs text-red-600" data-testid="wizard-descricao-erro">Descrição obrigatória, com pelo menos 5 caracteres</div>
              )}
            </div>
          </div>
        )}

        {/* PASSO 2 */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Adicionar peça ou serviço</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <Input
                  data-testid="wizard-busca-item"
                  className="pl-8"
                  placeholder="Buscar por código ou descrição..."
                  value={itensBusca}
                  onChange={(e) => setItensBusca(e.target.value)}
                />
                {itensSug.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {itensSug.map((it) => (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => adicionarItem(it)}
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b text-sm"
                        data-testid={`sug-item-${it.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <span className="font-mono text-xs mr-2">{it.codigo}</span>
                            {it.descricao}
                          </div>
                          <div className="text-xs font-mono text-orange-700">{fmtBRL(it.valor_unitario)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {itens.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="w-20 text-center">Qtd</TableHead>
                    <TableHead className="w-32 text-right">V. Unit.</TableHead>
                    <TableHead className="w-32 text-right">Total</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((it, idx) => (
                    <TableRow key={idx} data-testid={`wizard-item-row-${idx}`}>
                      <TableCell>
                        <div className="text-sm">{it.descricao}</div>
                        <div className="text-[10px] font-mono text-slate-500">{it.codigo} · {it.unidade}</div>
                      </TableCell>
                      <TableCell>
                        <Input
                          data-testid={`wizard-item-qtd-${idx}`}
                          type="number" min="0.01" step="0.01"
                          value={it.quantidade}
                          onChange={(e) => atualizarItem(idx, "quantidade", e.target.value)}
                          className="h-8 text-center"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          data-testid={`wizard-item-valor-${idx}`}
                          type="number" min="0" step="0.01"
                          value={it.valor_unitario}
                          onChange={(e) => atualizarItem(idx, "valor_unitario", e.target.value)}
                          className="h-8 text-right"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {fmtBRL((Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0))}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removerItem(idx)}
                          className="text-red-600 h-7 w-7 p-0" data-testid={`wizard-item-rm-${idx}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Desconto (%)</Label>
                <Input
                  data-testid="wizard-desconto"
                  type="number" min="0" max="100" step="0.01"
                  value={descontoPct}
                  onChange={(e) => setDescontoPct(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Forma pagamento</Label>
                <Select value={formaPag} onValueChange={setFormaPag}>
                  <SelectTrigger data-testid="wizard-forma-pag"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a_vista">À vista</SelectItem>
                    <SelectItem value="parcelado">Parcelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Vencimento</Label>
                <Input
                  data-testid="wizard-vencimento"
                  type="date"
                  value={vencimento}
                  onChange={(e) => setVencimento(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider">Previsão de faturamento</Label>
              <Input
                data-testid="wizard-previsao-fat"
                type="date"
                value={previsaoFat}
                onChange={(e) => setPrevisaoFat(e.target.value)}
                className="max-w-xs"
              />
            </div>

            <div className="bg-orange-50 p-3 rounded text-right">
              <div className="text-xs text-slate-600">Subtotal: <span className="font-mono">{fmtBRL(totais.subtotal)}</span></div>
              <div className="text-xs text-slate-600">Desconto: <span className="font-mono">-{fmtBRL(totais.desconto)}</span></div>
              <div className="text-lg font-semibold text-orange-900 mt-1">
                Total: <span className="font-mono">{fmtBRL(totais.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* PASSO 3 — Revisão */}
        {step === 3 && (
          <div className="space-y-3 text-sm">
            <Card className="p-3 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cliente</div>
              <div className="font-medium">{cliente?.nome_fantasia || cliente?.razao_social}</div>
              <div className="text-xs text-slate-600">{cliente?.cnpj} · {cliente?.cidade}/{cliente?.uf}</div>
            </Card>
            {equipamento && (
              <Card className="p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Equipamento</div>
                <div className="text-xs">{equipamento.modelo || "—"} · Série {equipamento.serial || "—"}</div>
              </Card>
            )}
            <Card className="p-3 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tipo / Descrição</div>
              <Badge className="mb-2 text-[10px]">{TIPO_SERVICO.find((t) => t.value === tipoServico)?.label}</Badge>
              <div className="text-xs">{descProblema}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Itens ({itens.length})</div>
              <Table>
                <TableBody>
                  {itens.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-xs">{it.descricao}</TableCell>
                      <TableCell className="text-xs text-center">{it.quantidade}× {fmtBRL(it.valor_unitario)}</TableCell>
                      <TableCell className="text-xs text-right font-mono">
                        {fmtBRL((Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="text-right mt-2 font-semibold text-orange-900">
                Total: {fmtBRL(totais.total)}
              </div>
            </Card>
            <Card className="p-3 bg-slate-50 grid grid-cols-3 gap-3 text-xs">
              <div><div className="text-[10px] uppercase text-slate-500">Forma</div><div>{formaPag === "a_vista" ? "À vista" : "Parcelado"}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Vencimento</div><div>{fmtData(vencimento)}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Prev. Fatur.</div><div>{fmtData(previsaoFat)}</div></div>
            </Card>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wizard-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            {step < 3 ? (
              <Button onClick={proximoPasso} data-testid="wizard-next"
                className="bg-orange-600 hover:bg-orange-700 text-white">
                Próximo <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : editar ? (
              <Button onClick={() => salvar(false)} disabled={saving} data-testid="wizard-save-edit"
                className="bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Salvar alterações
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => salvar(false)} disabled={saving}
                  data-testid="wizard-save-draft">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Salvar rascunho
                </Button>
                <Button onClick={() => salvar(true)} disabled={saving}
                  data-testid="wizard-send"
                  className="bg-orange-600 hover:bg-orange-700 text-white">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Enviar para cliente
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GerarOSModal({ open, onOpenChange, proposta, onGerada }) {
  const [tecnico, setTecnico] = useState("");
  const [dataExec, setDataExec] = useState("");
  const [obs, setObs] = useState("");
  const [nf, setNf] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTecnico(""); setDataExec(""); setObs(""); setNf(""); }
  }, [open]);

  const gerar = async () => {
    if (!proposta) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/posvenda/propostas/${proposta.id}/gerar-os`, {
        tecnico_nome: tecnico || null,
        data_execucao: dataExec || null,
        observacoes_tecnicas: obs || null,
        nota_fiscal: nf || null,
      });
      toast.success(`OS ${data.os_numero} gerada · ${(data.pdf_tamanho_bytes / 1024).toFixed(1)} KB`);
      onGerada && onGerada(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar OS");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="gerar-os-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-600" />
            Gerar Ordem de Serviço
          </DialogTitle>
          <DialogDescription className="text-xs">
            Complete os dados técnicos. Um PDF será gerado e a proposta passará para status <strong>OS GERADA</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Técnico responsável</Label>
            <Input data-testid="os-tecnico" value={tecnico}
              onChange={(e) => setTecnico(e.target.value)}
              placeholder="Nome do técnico (opcional)" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Data de execução prevista</Label>
            <Input data-testid="os-data-exec" type="date" value={dataExec}
              onChange={(e) => setDataExec(e.target.value)} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Observações técnicas</Label>
            <Textarea data-testid="os-obs" rows={3} value={obs}
              onChange={(e) => setObs(e.target.value)}
              placeholder="Ex: Necessário deslocamento de 50km, ferramentas especiais..." />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Nota Fiscal (opcional)</Label>
            <Input data-testid="os-nf" value={nf}
              onChange={(e) => setNf(e.target.value)}
              placeholder="Pode preencher depois" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={gerar} disabled={saving} data-testid="btn-confirm-gerar-os"
            className="bg-orange-600 hover:bg-orange-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
            Gerar OS + PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  DRAWER DETALHE
// ════════════════════════════════════════════════════════════════════════
function DetalheModal({ open, onOpenChange, propostaId, onAtualizado, onEditar }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gerarOpen, setGerarOpen] = useState(false);

  const recarregar = useCallback(async () => {
    if (!propostaId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/posvenda/propostas/${propostaId}`);
      setP(data);
    } catch (e) { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [propostaId]);

  useEffect(() => { if (open && propostaId) recarregar(); }, [open, propostaId, recarregar]);

  const mudarStatus = async (novo, motivo) => {
    try {
      await api.patch(`/posvenda/propostas/${propostaId}/status`, { novo_status: novo, motivo });
      toast.success(`Status: ${novo}`);
      onAtualizado && onAtualizado();
      const { data } = await api.get(`/posvenda/propostas/${propostaId}`);
      setP(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const canAprovar = ["diretor", "gerente", "supervisao"].includes(user?.role);
  const canGerarOS = ["diretor", "gerente", "supervisao", "operador"].includes(user?.role);
  const canEnviar = p && p.status === "rascunho" &&
    (["diretor", "gerente", "supervisao", "operador"].includes(user?.role) ||
     (user?.role === "vendedor" && p.vendedor_id === user?.id));

  const baixarPDF = async () => {
    if (!p?.os_id) return;
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("lab_token");
      const url = `${BACKEND}/api/posvenda/ordens-servico/${p.os_id}/pdf`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `OS_${p.os_numero}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const baixarPDFProposta = async () => {
    if (!p?.id) return;
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("lab_token");
      const url = `${BACKEND}/api/posvenda/propostas/${p.id}/pdf`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Proposta_${p.numero}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) {
      toast.error("Erro ao baixar Proposta PDF");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="detalhe-modal">
        {loading || !p ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <span className="font-mono">{p.numero}</span>
                <StatusBadge status={p.status} />
              </DialogTitle>
              <DialogDescription className="text-xs">
                Criada em {fmtData(p.criado_em)} por {p.criado_por_nome}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 text-sm">
              <Card className="p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Cliente</div>
                <div className="font-medium">{p.cliente_nome}</div>
                <div className="text-xs text-slate-600">{p.cliente_cnpj || "—"}</div>
              </Card>

              {p.equipamento_label && (
                <Card className="p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Equipamento</div>
                  <div className="text-xs">{p.equipamento_label}</div>
                </Card>
              )}

              <Card className="p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tipo / Descrição</div>
                <Badge className="mb-1 text-[10px]">{p.tipo_servico?.toUpperCase()}</Badge>
                <div className="text-xs whitespace-pre-line">{p.descricao_problema}</div>
              </Card>

              <Card className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Itens ({p.itens?.length || 0})</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-center w-20">Qtd</TableHead>
                      <TableHead className="text-right w-28">V. Unit.</TableHead>
                      <TableHead className="text-right w-28">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(p.itens || []).map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs">
                          <div>{it.descricao}</div>
                          <div className="text-[10px] font-mono text-slate-500">{it.codigo}</div>
                        </TableCell>
                        <TableCell className="text-xs text-center">{it.quantidade}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmtBRL(it.valor_unitario)}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{fmtBRL(it.valor_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="border-t mt-2 pt-2 text-right text-xs">
                  <div>Subtotal: <span className="font-mono">{fmtBRL(p.subtotal)}</span></div>
                  {p.desconto_total > 0 && <div>Desconto: <span className="font-mono">-{fmtBRL(p.desconto_total)}</span></div>}
                  <div className="text-lg font-semibold text-orange-900 mt-1">
                    Total: <span className="font-mono">{fmtBRL(p.total)}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Histórico</div>
                <div className="space-y-1">
                  {(p.historico || []).map((h, idx) => (
                    <div key={idx} className="text-xs flex items-start gap-2 border-l-2 border-orange-200 pl-2">
                      <div className="font-mono text-slate-500 w-28 flex-shrink-0">{fmtData(h.data)}</div>
                      <div>
                        <span className="font-medium">{h.status_anterior} → {h.status_novo}</span>
                        <div className="text-slate-600">{h.observacao} {h.user_nome ? `· ${h.user_nome}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              {canEnviar && (
                <Button onClick={() => mudarStatus("enviada")} data-testid="btn-enviar"
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Send className="w-4 h-4 mr-1" /> Enviar ao cliente
                </Button>
              )}
              {p.status === "enviada" && canAprovar && (
                <>
                  <Button onClick={() => mudarStatus("aprovada")} data-testid="btn-aprovar"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Aprovar
                  </Button>
                  <Button onClick={() => mudarStatus("recusada")} variant="outline"
                    data-testid="btn-recusar"
                    className="text-red-600 border-red-300 hover:bg-red-50">
                    <XCircle className="w-4 h-4 mr-1" /> Recusar
                  </Button>
                </>
              )}
              {p.status === "aprovada" && canGerarOS && (
                <Button data-testid="btn-gerar-os" onClick={() => setGerarOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white">
                  <FileText className="w-4 h-4 mr-1" /> Gerar OS
                </Button>
              )}
              {p.os_id && (
                <Button data-testid="btn-baixar-pdf-os" onClick={baixarPDF} variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50">
                  <FileText className="w-4 h-4 mr-1" /> Baixar PDF · {p.os_numero}
                </Button>
              )}
              <Button data-testid="btn-baixar-pdf-proposta" onClick={baixarPDFProposta} variant="outline"
                className="border-slate-300 text-slate-700 hover:bg-slate-50">
                <FileText className="w-4 h-4 mr-1" /> Proposta em PDF
              </Button>
              {["diretor", "gerente"].includes(user?.role) && ["rascunho", "enviada"].includes(p.status) && onEditar && (
                <Button data-testid="btn-editar-proposta" variant="outline" onClick={() => onEditar(p.id)}>
                  <Edit3 className="w-4 h-4 mr-1" /> Editar
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
      <GerarOSModal open={gerarOpen} onOpenChange={setGerarOpen} proposta={p}
        onGerada={() => { recarregar(); onAtualizado && onAtualizado(); }} />
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  PÁGINA PRINCIPAL
// ════════════════════════════════════════════════════════════════════════
export default function PosvendaPropostas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canDelete = ["diretor", "gerente"].includes(user?.role);
  const canCriar = ["diretor", "gerente", "supervisao", "operador", "vendedor"].includes(user?.role);

  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [buscaLista, setBuscaLista] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detalhe, setDetalhe] = useState({ open: false, id: null });
  const [delDialog, setDelDialog] = useState({ open: false, item: null });
  const [editando, setEditando] = useState(null);

  const abrirEdicao = async (id) => {
    try {
      const { data } = await api.get(`/posvenda/propostas/${id}`);
      setEditando(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível abrir a proposta");
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus !== "todos") params.status = filtroStatus;
      const { data } = await api.get("/posvenda/propostas", { params });
      setItems(data.items || []);
      setResumo(data.resumo || {});
    } catch (e) {
      toast.error("Erro ao listar propostas");
    } finally {
      setLoading(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  const excluir = async () => {
    if (!delDialog.item) return;
    try {
      await api.delete(`/posvenda/propostas/${delDialog.item.id}`);
      toast.success("Proposta excluída");
      setDelDialog({ open: false, item: null });
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div className="space-y-4" data-testid="posvenda-propostas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-orange-600" />
            Propostas Pós-venda
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Orçamentos de serviços técnicos · Funil: rascunho → enviada → aprovada → OS → concluída
          </p>
        </div>
        {canCriar && (
          <Button onClick={() => setWizardOpen(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="btn-nova-proposta">
            <Plus className="w-4 h-4 mr-2" /> Nova Proposta
          </Button>
        )}
      </div>

      {/* Resumo por status */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {Object.entries(STATUS_BADGE).map(([k, v]) => (
          <Card key={k} className="p-3 cursor-pointer hover:shadow-md transition"
            onClick={() => setFiltroStatus(k === filtroStatus ? "todos" : k)}
            data-testid={`kpi-${k}`}>
            <div className="text-[10px] uppercase text-slate-500 tracking-wider">{v.label}</div>
            <div className="text-2xl font-light text-slate-900">{resumo[k] || 0}</div>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Tabs value={filtroStatus} onValueChange={setFiltroStatus}>
            <TabsList>
              <TabsTrigger value="todos" data-testid="tab-todos">Todos</TabsTrigger>
              <TabsTrigger value="rascunho" data-testid="tab-rascunho">Rascunho</TabsTrigger>
              <TabsTrigger value="enviada" data-testid="tab-enviada">Enviadas</TabsTrigger>
              <TabsTrigger value="aprovada" data-testid="tab-aprovada">Aprovadas</TabsTrigger>
              <TabsTrigger value="os_gerada" data-testid="tab-os">OS Geradas</TabsTrigger>
              <TabsTrigger value="recusada" data-testid="tab-recusada">Recusadas</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input className="pl-8 h-9 text-xs"
              placeholder="Buscar nº, cliente ou CNPJ..."
              value={buscaLista}
              onChange={(e) => setBuscaLista(e.target.value)}
              data-testid="propostas-busca" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            Nenhuma proposta encontrada.
            {canCriar && <div className="mt-2">Clique em <strong>+ Nova Proposta</strong> para criar.</div>}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Nº</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="w-28">Tipo</TableHead>
                <TableHead className="w-32 text-right">Valor</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-28">Data</TableHead>
                <TableHead className="w-32 text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const q = buscaLista.trim().toUpperCase();
                const filtrados = q ? items.filter((p) =>
                  [p.numero, p.cliente_nome, p.cliente_cnpj, p.descricao_problema, p.tipo_servico]
                    .some((f) => String(f || "").toUpperCase().includes(q))
                ) : items;
                if (filtrados.length === 0) {
                  return (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                      Nenhum resultado para "{buscaLista}"
                    </TableCell></TableRow>
                  );
                }
                return filtrados.map((p) => (
                <TableRow key={p.id} data-testid={`row-proposta-${p.id}`}>
                  <TableCell className="font-mono text-xs">{p.numero}</TableCell>
                  <TableCell>
                    <div className="text-sm">{p.cliente_nome}</div>
                    <div className="text-[10px] text-slate-500">{p.cliente_cnpj || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-300 uppercase">
                      {p.tipo_servico}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtBRL(p.total)}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-xs text-slate-600">{fmtData(p.criado_em)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost"
                        onClick={() => setDetalhe({ open: true, id: p.id })}
                        data-testid={`btn-ver-${p.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {canDelete && ["rascunho", "enviada"].includes(p.status) && (
                        <Button size="sm" variant="ghost" onClick={() => abrirEdicao(p.id)}
                          data-testid={`btn-editar-${p.id}`} title="Editar">
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          onClick={() => setDelDialog({ open: true, item: p })}
                          data-testid={`btn-del-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        )}
      </Card>

      <WizardModal open={wizardOpen} onOpenChange={setWizardOpen} onSaved={carregar} />
      <WizardModal open={!!editando} onOpenChange={(o) => !o && setEditando(null)} onSaved={carregar} editar={editando} />
      <DetalheModal open={detalhe.open} onOpenChange={(o) => setDetalhe((d) => ({ ...d, open: o }))}
        propostaId={detalhe.id} onAtualizado={carregar}
        onEditar={(id) => { setDetalhe({ open: false, id: null }); abrirEdicao(id); }} />

      <Dialog open={delDialog.open} onOpenChange={(o) => setDelDialog((d) => ({ ...d, open: o }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir proposta?</DialogTitle>
            <DialogDescription>
              {delDialog.item ? `Proposta ${delDialog.item.numero} (${delDialog.item.cliente_nome})` : ""} será excluída (soft delete).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDialog({ open: false, item: null })}>Cancelar</Button>
            <Button onClick={excluir} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-del-proposta">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
